import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * Global busy/loader state.
 * Also suppresses local `.page-load` overlays after gateway errors so the UI
 * cannot stay frozen when a component forgot an HTTP error handler.
 */
@Injectable({
  providedIn: 'root',
})
export class LoaderService {
  private readonly busyClearedClass = 'http-busy-cleared';

  /** Prefer BehaviorSubject so late subscribers see the current state. */
  isLoading = new BehaviorSubject<boolean>(false);

  /** Fired when busy UI must be cleared (e.g. 502/504). Components may subscribe to reset local flags. */
  readonly busyReset$ = new Subject<void>();

  constructor(router: Router) {
    router.events.pipe(filter((e): e is NavigationStart => e instanceof NavigationStart)).subscribe(() => {
      this.allowPageLoadOverlays();
    });
  }

  show() {
    this.allowPageLoadOverlays();
    this.isLoading.next(true);
  }

  hide() {
    this.isLoading.next(false);
  }

  /**
   * Hide the global loader and suppress local `.page-load` overlays.
   * Used for gateway errors so the screen never stays stuck behind a spinner.
   */
  clearBusyUi(): void {
    this.hide();
    this.busyReset$.next();
    if (typeof document !== 'undefined') {
      document.body.classList.add(this.busyClearedClass);
    }
  }

  allowPageLoadOverlays(): void {
    if (typeof document !== 'undefined') {
      document.body.classList.remove(this.busyClearedClass);
    }
  }
}
